import { isRunningOnWorker } from 'owa-config/lib/isRunningOnWorker';
import { hasQueryStringParameter } from 'owa-querystring';
import { getWindowData } from 'owa-window-data';
import type { InternalRequestOptions } from './RequestOptions';

/**
 * PAS (aka.ms/PAS) records each Outlook boot once through a Web Page Replay (WPR) proxy and then
 * replays the saved responses for the measured runs. During replay WPR picks the saved response by
 * request URL only (POST bodies are ignored), so per-session URL values such as the request counter
 * (`n`), correlation vector (`cv`) and correlation id (`cri`) make requests receive responses that
 * belong to other requests.
 *
 * When the page URL has `?pas` (both the recording run and the replay runs), request builders keep
 * those values out of the URL (they are still sent as headers) and add a fingerprint of the request
 * payload and selected routing/response-shaping headers instead. Endpoint-owned projections can
 * exclude tracking fields from identity without changing the actual body sent to the service.
 */
export const PAS_REPLAY_KEY_PARAM = 'pasKey';
export const PAS_REPLAY_OPERATION_PARAM = 'pasOp';

let cachedIsPasSession: boolean | undefined;

export function isPasSession(): boolean {
    if (cachedIsPasSession === undefined) {
        // On the data worker the page location only becomes available once the main thread has sent
        // its window data, so don't cache a result computed before that.
        if (!getWindowData()?.location) {
            return false;
        }

        cachedIsPasSession = hasQueryStringParameter('pas');
    }

    return cachedIsPasSession;
}

/**
 * Keeps PAS body scopes repeatable without combining separate renderers or main/worker scopes.
 * Attachments also need an identity suffix so nested previews do not share their parent's styles.
 * Call after checking isPasSession() at request construction, not during module initialization.
 */
export function getPasCssScopeClassName(scopeName: string, itemId?: string): string {
    const thread = isRunningOnWorker() ? 'worker' : 'main';
    const itemScope = itemId === undefined ? '' : '_' + getPasReplayKey(itemId);
    return `rps_pas_${thread}_${scopeName}${itemScope}`;
}

/**
 * Returns a short, repeatable fingerprint of a request payload: the same text always gives the same
 * number, and different text almost always gives a different one. It only needs to tell this
 * session's requests apart, so it uses the classic "multiply by 31 and add the next character"
 * string hash (the same formula as Java's String.hashCode) instead of a cryptographic hash, which
 * would add a library to the startup bundle for every user.
 * Byte inputs use the same recurrence; existing string fingerprints are unchanged.
 */
export function getPasReplayKey(payload: string | Uint8Array): string {
    const range = 2 ** 32;
    let hash = 0;
    for (let i = 0; i < payload.length; i++) {
        // Wrapping around at 2^32 keeps the number small enough to stay exact.
        const value = typeof payload === 'string' ? payload.charCodeAt(i) : payload[i];
        hash = (hash * 31 + value) % range;
    }

    return String(hash);
}

// Deliberately allowlist response/routing inputs. Never fingerprint Authorization, cookies,
// canaries, traceparent, correlation vectors or per-session diagnostic IDs.
const replayHeaderNames = [
    'accept',
    'accept-language',
    'content-type',
    'prefer',
    'x-anchormailbox',
    'x-owa-explicitlogonuser',
    'x-auxmailbox',
    'x-tenantid',
    'x-req-source',
    'x-owa-hosted-ux',
    'x-owa-host-app',
    'x-owa-hosted-scenario',
    'x-client-flights',
    'x-client-language',
    'x-scenario',
    'x-search-griffin-version',
    'x-search-shadow',
    'x-ms-caller-name',
];

const startupReplayHeaderNames = [
    'x-js-experiment',
    'x-message-count',
    'x-folder-count',
    'x-calendar-folders',
    'x-calendar-view',
    'x-calculate-range-from-viewtype',
    'folderparams',
    'messageparams',
    'calendarfoldersparams',
    'calendarviewparams',
];

/**
 * Call only in PAS, after options preparation has serialized the wire body and set routing.
 * A caller-supplied body descriptor must preserve semantic fields and exclude credentials.
 * This is a replay discriminator, not anonymization or a guarantee against hash collisions.
 */
export async function getPasRequestKey(
    actionName: string,
    options: Pick<InternalRequestOptions, 'method' | 'headers' | 'body' | 'mailboxInfo'>,
    replayBody?: string
): Promise<string> {
    // Snapshot mutable uploads before the first await, and send that same snapshot. Blob/File
    // and strings are immutable; streams cannot be inspected without consuming the upload.
    options.body = snapshotPasBody(options.body);
    const headerNames = [...replayHeaderNames];
    if (actionName === 'StartupData') {
        headerNames.push(...startupReplayHeaderNames);
    } else if (actionName === 'CreateMessageFromFile') {
        headerNames.push('x-unsent');
    }
    const { mailboxInfo } = options;
    const identity = [
        actionName,
        (options.method ?? 'GET').toUpperCase(),
        mailboxInfo && [
            mailboxInfo.type,
            mailboxInfo.userIdentity,
            mailboxInfo.mailboxSmtpAddress,
            mailboxInfo.mailboxPrefixedIdentity,
            mailboxInfo.auxiliaryMailboxGuid,
        ],
        headerNames.map(name => [name, options.headers.get(name)]),
        options.headers.get('x-owa-urlpostdata'),
    ];
    return getPasReplayKey(
        JSON.stringify([...identity, await getPasBodyIdentity(replayBody ?? options.body)])
    );
}

function snapshotPasBody(body: BodyInit | undefined): BodyInit | undefined {
    if (body instanceof ArrayBuffer) {
        return body.slice(0);
    }
    if (ArrayBuffer.isView(body)) {
        return new Uint8Array(body.buffer, body.byteOffset, body.byteLength).slice();
    }
    if (body instanceof URLSearchParams) {
        return new URLSearchParams(body);
    }
    if (body instanceof FormData) {
        const snapshot = new FormData();
        for (const [name, value] of body.entries()) {
            snapshot.append(name, value);
        }
        return snapshot;
    }
    return body;
}

async function getPasBodyIdentity(body: BodyInit | undefined): Promise<unknown> {
    if (body === undefined || body === null || typeof body === 'string') {
        return body ?? null;
    }
    if (body instanceof Blob) {
        return [
            'blob',
            body.type,
            body.size,
            getPasReplayKey(new Uint8Array(await body.arrayBuffer())),
        ];
    }
    if (body instanceof ArrayBuffer || ArrayBuffer.isView(body)) {
        const bytes =
            body instanceof ArrayBuffer
                ? new Uint8Array(body)
                : new Uint8Array(body.buffer, body.byteOffset, body.byteLength);
        return ['bytes', bytes.length, getPasReplayKey(bytes)];
    }
    if (body instanceof URLSearchParams) {
        return ['urlencoded', body.toString()];
    }
    if (body instanceof FormData) {
        const entries = Array.from(body.entries());
        return [
            'form',
            await Promise.all(
                entries.map(async ([name, value]) => [
                    name,
                    typeof value === 'string' ? undefined : value.name,
                    await getPasBodyIdentity(value),
                ])
            ),
        ];
    }
    // Reading a stream here would consume the upload. Do not silently send an ambiguous PAS URL.
    throw new Error('PAS replay identity does not support streaming request bodies');
}
